import { faMinus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Form, { Field as BaseField, Field, List } from "rc-field-form";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "reactstrap";
import Button from "../../../../../../../../../components/Button";
import FieldWrap from "../../../../../../../../../components/Field";
import Checkbox from "../../../../../../../../../components/Inputs/Checkbox";
import HourTypeSelect from "../../../../../../../../../components/Inputs/HourTypeSelect";
import ResourceSelect from "../../../../../../../../../components/Inputs/ResourceSelect";
import Text from "../../../../../../../../../components/Inputs/Text";
import {
	getPathWithParams,
	getSettingsResourceSearchFilters,
	renderSettingsResourceLabel,
} from "../../../../../../../../../utils/helpers/settings";
import { payCodeCalcFields } from "../../../../../helper";
import PayCodeSelect from "../../../PayCodeSelect";

function OvertimeBandItem({
	name,
	disabled,
	overtimes,
	setFieldValue,
	getFieldValue,
}) {
	const { t } = useTranslation();
	const splitOvertime = getFieldValue("splitOvertime");

	const remove = useCallback(
		(index) => {
			const data = overtimes
				.filter((_, i) => i !== index)
				.map((overtime, i, array) => {
					const previous = array[i - 1];
					return { ...overtime, start: previous?.end ? Number(previous.end) + 1 : 0 };
				});

			setFieldValue("overtimes", data);
		},
		[overtimes, setFieldValue],
	);

	// {current}.start = {current - 1}.end + 1
	// {current}.end = {current + 1}.start - 1
	// labels are added separately so we can display all the input errors below the row on a single place

	return (
		<div className="d-flex flex-row align-items-center gap-2 mb-2">
			<div>
				<Label for={[name, "start"]}>
					{t("start")}
					<small className="text-red"> *</small>
				</Label>
				<Field
					name={[name, "start"]}
					dependencies={[
						["overtimes", name, "end"],
						["overtimes", name - 1, "end"],
					]}
					rules={[
						{
							required: true,
							message: t("required-start"),
						},
						{
							validator: (_, value) => {
								if (value) {
									if (Number(value) < 0) {
										return Promise.reject(new Error(t("start-bigger-than-or-equal-to-zero")));
									}
									const currentEnd = getFieldValue(["overtimes", name, "end"]);

									if (
										!isNaN(currentEnd)
										&& currentEnd !== null
										&& currentEnd !== ""
										&& Number(value) >= Number(currentEnd)
									) {
										return Promise.reject(new Error(t("timeoff-start-smaller-than-end")));
									}
								}
								return Promise.resolve();
							},
						},
					]}
				>
					{({ value, onChange }) => {
						return (
							<Text
								id={[name, "start"]}
								type="number"
								disabled={disabled}
								placeholder={t("start")}
								className="w-auto"
								value={value}
								onChange={(e) => {
									const value = e.target.value === '' ? undefined : Number(e.target.value);
									onChange(value);

									const previous = getFieldValue(["overtimes", name - 1]);
									if (!previous || (!value && value !== 0)) {
										return;
									}
									setFieldValue(["overtimes", name - 1, "end"], value - 1);
								}}
							/>
						);
					}}
				</Field>
			</div>

			<div>
				<Label for={[name, "end"]}>
					{t("end")}
					<small className="text-red"> *</small>
				</Label>
				<Field
					name={[name, "end"]}
					dependencies={[
						["overtimes", name, "start"],
						["overtimes", name + 1, "start"],
					]}
					rules={[
						{
							required: name < overtimes.length - 1,
							message: t("required-end"),
						},
						{
							validator: (_, value) => {
								if (value) {
									if (Number(value) < 0) {
										return Promise.reject(new Error(t("end-bigger-than-zero")));
									}
									const currentStart = getFieldValue(["overtimes", name, "start"]);

									if (
										!isNaN(currentStart) &&
										currentStart !== "" &&
										currentStart !== null &&
										Number(value) <= Number(currentStart)
									) {
										return Promise.reject(new Error(t("timeoff-end-bigger-than-start")));
									}
								}
								return Promise.resolve();
							},
						},
					]}
				>
					{({ value, onChange }) => {
						return (
							<Text
								id={[name, "end"]}
								type="number"
								disabled={disabled}
								placeholder={t("end")}
								className="w-auto"
								value={value}
								onChange={(e) => {
									const value = e.target.value === '' ? undefined : Number(e.target.value);
									onChange(value);

									const next = getFieldValue(["overtimes", name + 1]);
									if (!next || (!value && value !== 0)) {
										return;
									}
									setFieldValue(["overtimes", name + 1, "start"], value + 1);
								}}
							/>
						);
					}}
				</Field>
			</div>

			<div className="flex-fill">
				<Label for={[name, "payCode"]}>
					{t("pay-code")}
					<small className="text-red"> *</small>
				</Label>

				<Field
					name={[name, "payCode"]}
					rules={[
						{
							required: true,
							message: t("required-pay-code"),
						},
					]}
				>
					<ResourceSelect
						id={[name, "payCode"]}
						resourcePath={getPathWithParams("/pay-code", true, {}, payCodeCalcFields)}
						hasSearch
						renderLabel={renderSettingsResourceLabel}
						getSearchFilters={getSettingsResourceSearchFilters}
						placeholder={t("pay-code")}
						disabled={disabled}
					/>
				</Field>
			</div>

			<div className="flex-fill">
				<Label for={[name, "premiumPayCode"]}>
					{t("premium-pay-code")}
					<small className="text-red"> *</small>
				</Label>

				{splitOvertime && (
					<Field
						name={[name, "premiumPayCode"]}
						dependencies={[["splitOvertime"]]}
						rules={[
							{
								required: true,
								message: t("required-premium-pay-code"),
							},
						]}
					>
						<ResourceSelect
							id={[name, "premiumPayCode"]}
							renderLabel={renderSettingsResourceLabel}
							resourcePath={getPathWithParams("/pay-code", true, {}, payCodeCalcFields, {
								hourType: "unit",
							})}
							hasSearch
							getSearchFilters={getSettingsResourceSearchFilters}
							placeholder={t("premium-pay-code")}
							disabled={disabled}
						/>
					</Field>
				)}
			</div>

			{!disabled && (
				<FontAwesomeIcon
					icon={faMinus}
					className="text-danger cursor-pointer pt-3"
					onClick={() => remove(name)}
				/>
			)}
		</div>
	);
}

function OvertimesList({
	overtimes,
	defaultPayCode,
	disabled,
	setFieldValue,
	getFieldValue,
	getFieldsError,
}) {
	const { t } = useTranslation();

	return (
		<List name="overtimes">
			{(data, { add }) => {
				const addItem = () => {
					const values = { payCode: defaultPayCode };
					if (data.length > 0) {
						const previous = overtimes[overtimes.length - 1];
						values.start = previous?.end && Number(previous.end) + 1;
					}
					add(values);
				};

				return (
					<div>
						{data.map(({ name }) => {
							const splitOvertime = getFieldValue("splitOvertime");
							const overtimeFieldPaths = [
								["overtimes", name, "start"],
								["overtimes", name, "end"],
								["overtimes", name, "payCode"],
							];
							if (splitOvertime) {
								overtimeFieldPaths.push(["overtimes", name, "premiumPayCode"]);
							}
							const [error] = getFieldsError(overtimeFieldPaths)
								.map(({ errors }) => errors)
								.flat();

							return (
								<div key={name}>
									<OvertimeBandItem
										name={name}
										disabled={disabled}
										overtimes={overtimes}
										setFieldValue={setFieldValue}
										getFieldValue={getFieldValue}
									/>
									<div className="invalid-feedback d-block">{error}</div>
								</div>
							);
						})}

						{!disabled && (
							<Button color="primary" className="w-100 mb-2" type="button" onClick={addItem}>
								+ {t("add")}
							</Button>
						)}
					</div>
				);
			}}
		</List>
	);
}

function ParametersForm({ overtimeKey, close, overtime, disabled }) {
	const { t } = useTranslation();
	const [hidden, setHidden] = useState(false);
	const onTogglePayCodes = useCallback((e) => setHidden(!e.target.checked), []);

	return (
		<Form
			name={overtimeKey}
			onFinish={close}
			labelAlign="left"
			labelCol={{ span: 8 }}
			wrapperCol={{ span: 14, offset: 2 }}
			colon={false}
			initialValues={overtime}
			scrollToFirstError
		>
			<FieldWrap
				name="value"
				label={t("day-to-look")}
				rules={[
					{
						required: true,
						message: t("required-consecutive-day"),
					},
				]}
			>
				<Text type="number" placeholder={t("consecutive-day")} disabled />
			</FieldWrap>

			<FieldWrap
				label={t("default-pay-code")}
				name="defaultPayCode"
				rules={[
					{
						required: true,
						message: t("required-default-pay-code"),
					},
				]}
			>
				<ResourceSelect
					labelPropName="description"
					renderLabel={renderSettingsResourceLabel}
					resourcePath={getPathWithParams("/pay-code", true, {}, payCodeCalcFields)}
					placeholder={t("default-pay-code")}
					hasSearch
					getSearchFilters={getSettingsResourceSearchFilters}
					disabled={disabled}
				/>
			</FieldWrap>

			<div className="mb-3">
				<BaseField name="splitOvertime" valuePropName="checked">
					<Checkbox id="splitOvertime" disabled={disabled}>
						{t("split-overtime")}
					</Checkbox>
				</BaseField>
			</div>

			<BaseField shouldUpdate>
				{(_, __, { getFieldValue }) => {
					const splitOvertime = getFieldValue("splitOvertime");
					return (
						splitOvertime && (
							<FieldWrap
								label={t("premium-pay-code")}
								name="premiumPayCode"
								rules={[
									{
										required: true,
										message: t("required-premium-pay-code"),
									},
								]}
							>
								<ResourceSelect
									labelPropName="description"
									renderLabel={renderSettingsResourceLabel}
									resourcePath={getPathWithParams("/pay-code", true, {}, payCodeCalcFields, {
										hourType: "unit",
									})}
									placeholder={t("premium-pay-code")}
									hasSearch
									getSearchFilters={getSettingsResourceSearchFilters}
									disabled={disabled}
								/>
							</FieldWrap>
						)
					);
				}}
			</BaseField>

			<FieldWrap name="calculateOtherOvertime" valuePropName="checked">
				<Checkbox id="calculate-other-overtime-checkbox">
					{t("calculate-other-overtime")}
				</Checkbox>
			</FieldWrap>

			<BaseField shouldUpdate>
				{(_, __, { getFieldValue, setFieldValue, getFieldsError }) => {
					const calculateOtherOvertime = getFieldValue("calculateOtherOvertime");
					const bandList = getFieldValue("overtimes");
					const defaultPayCode = getFieldValue("defaultPayCode");
					return calculateOtherOvertime && (
						<OvertimesList
							overtimes={bandList}
							defaultPayCode={defaultPayCode}
							disabled={disabled}
							setFieldValue={setFieldValue}
							getFieldValue={getFieldValue}
							getFieldsError={getFieldsError}
						/>
					);
				}}
			</BaseField>

			<BaseField shouldUpdate noStyle>
				{(_, __, { getFieldValue, setFieldValue }) => {
					const payCodes = getFieldValue("payCodes");
					const onHourTypesChange = (hourTypes) => {
						setFieldValue(
							"payCodes",
							payCodes?.filter((payCode) => hourTypes.includes(payCode.hourType)),
						);
						setFieldValue("unitPayCode", undefined);
					};
					return (
						<FieldWrap label={t("eligible-hour-types")} name="hourTypes">
							<HourTypeSelect
								placeholder={t("eligible-hour-types")}
								mode="multiple"
								onChange={onHourTypesChange}
								disabled={disabled}
							/>
						</FieldWrap>
					);
				}}
			</BaseField>

			<BaseField shouldUpdate>
				{(_, __, { getFieldValue }) => {
					const hourTypes = getFieldValue("hourTypes");
					return (
						hourTypes?.includes("unit") && (
							<FieldWrap
								label={t("unit-pay-code")}
								name="unitPayCode"
								rules={[
									{
										required: true,
										message: t("required-unit-pay-code"),
									},
								]}
							>
								<ResourceSelect
									labelPropName="description"
									renderLabel={renderSettingsResourceLabel}
									resourcePath={getPathWithParams("/pay-code", true, {}, payCodeCalcFields, {
										hourType: "unit",
									})}
									placeholder={t("unit-pay-code")}
									hasSearch
									getSearchFilters={getSettingsResourceSearchFilters}
									disabled={disabled}
								/>
							</FieldWrap>
						)
					);
				}}
			</BaseField>

			<div className="mb-3">
				<Checkbox checked={!hidden} onChange={onTogglePayCodes} id="show-pay-code" disabled={disabled}>
					{t("show-eligible-pay-code")}
				</Checkbox>
			</div>

			<BaseField shouldUpdate noStyle>
				{(_, __, { getFieldValue }) => {
					if (hidden) {
						return null;
					}
					return (
						<PayCodeSelect
							hourTypes={getFieldValue("hourTypes")}
							name="payCodes"
							disabled={disabled}
						/>
					);
				}}
			</BaseField>

			<div className="d-flex justify-content-between my-2">
				{disabled ? (
					<div />
				) : (
					<Button type="submit" color="primary" size="xs" className="bg-dark">
						{t("save-changes")}
					</Button>
				)}
				<Button size="small" className="border-primary-color mr-1" onClick={close}>
					{t("close")}
				</Button>
			</div>
		</Form>
	);
}

export default ParametersForm;
